import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { EXCEL_CONFIG } from '../constants';

const formatDate = (unixTimestamp) => {
  const date = new Date((unixTimestamp + 86000) * 1000);
  return `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
};

const createHeaderRow = (worksheet, fechasOrdenadas) => {
  const headers = [...EXCEL_CONFIG.HEADERS, ...fechasOrdenadas];
  worksheet.addRow(headers);
  
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: EXCEL_CONFIG.COLORS.HEADER }
  };
  
  headerRow.eachCell(cell => {
    cell.border = {
      top: { style: 'thin', color: { argb: EXCEL_CONFIG.COLORS.BORDER } },
      left: { style: 'thin', color: { argb: EXCEL_CONFIG.COLORS.BORDER } },
      bottom: { style: 'thin', color: { argb: EXCEL_CONFIG.COLORS.BORDER } },
      right: { style: 'thin', color: { argb: EXCEL_CONFIG.COLORS.BORDER } }
    };
  });
};

const processDataForExcel = (datos) => {
  const fechasUnicasMap = new Map();
  datos.forEach(item => {
    item.meses.forEach(month => {
      // Guardamos tanto el timestamp como el formato formateado
      fechasUnicasMap.set(month.mes_unix, formatDate(month.mes_unix));
    });
  });
  
  // Ordenamos por el timestamp (clave del Map) y devolvemos los valores formateados
  return Array.from(fechasUnicasMap.keys())
    .sort((a, b) => a - b)
    .map(timestamp => fechasUnicasMap.get(timestamp));
};


const applyRowStyles = (row, isAlternate) => {
  const fillColor = isAlternate ? EXCEL_CONFIG.COLORS.ALTERNATE_ROW : EXCEL_CONFIG.COLORS.WHITE;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: fillColor }
    };
    cell.border = {
      top: { style: 'thin', color: { argb: EXCEL_CONFIG.COLORS.BORDER } },
      left: { style: 'thin', color: { argb: EXCEL_CONFIG.COLORS.BORDER } },
      bottom: { style: 'thin', color: { argb: EXCEL_CONFIG.COLORS.BORDER } },
      right: { style: 'thin', color: { argb: EXCEL_CONFIG.COLORS.BORDER } }
    };
  });
};

export const useExcelExport = () => {
  const exportToExcel = async (data) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(EXCEL_CONFIG.SHEET_NAME);
    
    const fechasOrdenadas = processDataForExcel(data.data.datos);
    createHeaderRow(worksheet, fechasOrdenadas);

    let currentMaterial = null;
    let isAlternateColor = false;

    data.data.datos.forEach(item => {
      if (currentMaterial !== item.material) {
        currentMaterial = item.material;
        isAlternateColor = !isAlternateColor;
      }

      const valoresPorVariable = Object.keys(EXCEL_CONFIG.VARIABLES).reduce((acc, key) => {
        acc[EXCEL_CONFIG.VARIABLES[key]] = {};
        return acc;
      }, {});

      item.meses.forEach(month => {
        const monthYear = formatDate(month.mes_unix);
        valoresPorVariable['Ajuste Demanda'][monthYear] = month.ajuste_demanda ?? 0;
        valoresPorVariable.Baseline[monthYear] = month.baseline ?? 0;
        valoresPorVariable.Category[monthYear] = month.category ?? 0;
        valoresPorVariable.Gerente[monthYear] = month.gerente ?? 0;
        valoresPorVariable.Sincronizacion[monthYear] = month.sincronizacion ?? 0;
        valoresPorVariable.Junta[monthYear] = month.junta ?? 0;
        valoresPorVariable.Demanda[monthYear] = month.final ?? 0;
      });

      Object.entries(valoresPorVariable).forEach(([variable, valores]) => {
        const rowData = [
          item.material, 
          item.texto || '', 
          item.categoria || '', 
          item.subcategoria || '', 
          item.familia || '', 
          item.marca || '', 
          item.modelo || '', 
          variable
        ];
        fechasOrdenadas.forEach(fecha => {
          rowData.push(valores[fecha] ?? 0);
        });
        
        const row = worksheet.addRow(rowData);
        applyRowStyles(row, isAlternateColor);
      });
    });

    worksheet.columns.forEach(column => {
      column.width = 15;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    saveAs(blob, 'datos_consolidados.xlsx');
  };

  return { exportToExcel };
};
